import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { useSearchParams } from 'react-router-dom';

import type { CompanyListProps } from '@services/CompanySetupService/dtos/ListCompanyDTO';
import CompanySetupService from '@services/CompanySetupService';

import { toastifyApiErrors } from '@utils';
import { useFilterParams } from '@hooks';
import { ITEMS_LIMIT_PER_PAGE } from '@constants';
import { CompanySetupQueryKeysEnum } from 'constants/private/companySetup/companySetupQueryKeys';
import { loadParamsBasedOnKeys } from 'hooks/services/beneficiaries/list/utils';
import { FilterParamsEnum } from 'constants/private/global/filterParamsEnum';

export function useCompanySetupListState() {
  const { searchParam, currentPage, mappedFilters } = useFilterParams();
  const [searchParams] = useSearchParams();

  const mappedParams = loadParamsBasedOnKeys(searchParams, [
    FilterParamsEnum.STATUS,
  ]);

  const { enqueueSnackbar } = useSnackbar();

  const companySetupService = useMemo(() => new CompanySetupService(), []);

  const companySetupListState = useQuery<CompanyListProps>({
    queryKey: [
      CompanySetupQueryKeysEnum.COMPANY_SETUP_LIST_STATE,
      currentPage,
      mappedFilters,
      mappedParams,
      searchParam,
    ],
    queryFn: () =>
      companySetupService.getCompanies({
        ...searchParam,
        ...mappedFilters,
        ...mappedParams,
        offset: ITEMS_LIMIT_PER_PAGE * (currentPage - 1),
        limit: ITEMS_LIMIT_PER_PAGE,
      }),
    onError: (error: unknown) => {
      toastifyApiErrors(error, enqueueSnackbar);
    },
  });

  const refreshCompanySetupListStates = () => {
    companySetupListState?.remove();
    companySetupListState?.refetch();
  };

  return {
    companySetupListState,
    refreshCompanySetupListStates,
  };
}
